import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, XCircle, TrendingUp } from "lucide-react";

interface KeywordAnalysis {
  exact_matches: string[];
  missing_keywords: string[];
  keyword_density: number;
  context_score: number;
}

interface ATSKeywordAnalysisProps {
  analysis: KeywordAnalysis;
}

export const ATSKeywordAnalysis = ({ analysis }: ATSKeywordAnalysisProps) => {
  const densityStatus = 
    analysis.keyword_density >= 2 && analysis.keyword_density <= 4 
      ? "optimal" 
      : analysis.keyword_density > 4 
      ? "too-high" 
      : "too-low";

  return (
    <div className="space-y-4">
      {/* Keyword Density */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4">Keyword Density</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm">Current Density:</span>
            <span className="text-2xl font-bold">{analysis.keyword_density.toFixed(1)}%</span>
          </div>
          <Progress value={analysis.keyword_density * 20} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0%</span>
            <span className="text-green-600 font-semibold">2-4% (Optimal)</span>
            <span>5%+</span>
          </div>
          {densityStatus === "optimal" && (
            <div className="p-3 bg-muted/50 border border-green-600/30 rounded-lg text-sm text-green-700 dark:text-green-400 font-medium">
              ✓ Your keyword density is in the optimal range
            </div>
          )}
          {densityStatus === "too-high" && (
            <div className="p-3 bg-muted/50 border border-red-600/30 rounded-lg text-sm text-red-700 dark:text-red-400 font-medium">
              ⚠ Keyword density is too high. This may be flagged as keyword stuffing.
            </div>
          )}
          {densityStatus === "too-low" && (
            <div className="p-3 bg-muted/50 border border-yellow-600/30 rounded-lg text-sm text-yellow-700 dark:text-yellow-400 font-medium">
              ⚠ Keyword density is low. Consider adding more relevant keywords naturally.
            </div>
          )}
        </div>
      </Card>

      {/* Context Score */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4">Contextual Relevance</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm">Keywords used in proper context:</span>
            <span className="text-2xl font-bold">{analysis.context_score}%</span>
          </div>
          <Progress value={analysis.context_score} />
          <p className="text-sm text-muted-foreground">
            This measures whether your keywords appear in meaningful contexts (work experience, achievements) 
            rather than just being listed in a skills section.
          </p>
        </div>
      </Card>

      {/* Matched Keywords */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          Matched Keywords ({analysis.exact_matches.length})
        </h3>
        <div className="flex flex-wrap gap-2">
          {analysis.exact_matches.map((keyword, index) => (
            <Badge key={index} variant="outline" className="bg-muted border-green-600 text-green-700 dark:text-green-400">
              {keyword}
            </Badge>
          ))}
        </div>
        {analysis.exact_matches.length === 0 && (
          <p className="text-muted-foreground text-sm">No exact keyword matches found</p>
        )}
      </Card>

      {/* Missing Keywords */}
      <Card className="p-6 border-yellow-500/50">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <XCircle className="h-6 w-6 text-yellow-600" />
          Missing Critical Keywords ({analysis.missing_keywords.length})
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          These important keywords from the job description are not in your resume.
        </p>
        <div className="flex flex-wrap gap-2">
          {analysis.missing_keywords.map((keyword, index) => (
            <Badge key={index} variant="outline" className="bg-muted border-yellow-600 text-yellow-700 dark:text-yellow-400">
              {keyword}
            </Badge>
          ))}
        </div>
        {analysis.missing_keywords.length === 0 && (
          <p className="text-green-600 text-sm">✓ All critical keywords are present!</p>
        )}
      </Card>
    </div>
  );
};
